import React, { useState, useEffect } from 'react';
import './SupplierManagement.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SupplierManagement = ({ token }) => {
  console.log('SupplierManagement token prop:', token);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    location: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    if (token) {
      fetchSuppliers();
    }
  }, [token]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/suppliers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch suppliers');
      const data = await response.json();
      setSuppliers(Array.isArray(data) ? data : (data.suppliers || []));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${API_URL}/suppliers/${editingId}`
        : `${API_URL}/suppliers`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save supplier');
      
      await fetchSuppliers();
      setFormData({
        name: '',
        code: '',
        location: '',
        contact_email: '',
        contact_phone: ''
      });
      setShowForm(false);
      setEditingId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (supplier) => {
    setFormData({
      name: supplier.supplier_name,
      code: supplier.supplier_code,
      location: supplier.address,
      contact_email: supplier.email || '',
      contact_phone: supplier.phone || ''
    });
    setEditingId(supplier.supplier_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;

    try {
      const response = await fetch(`${API_URL}/suppliers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete supplier');
      await fetchSuppliers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      code: '',
      location: '',
      contact_email: '',
      contact_phone: ''
    });
  };

  return (
    <div className="supplier-management">
      <div className="supplier-header">
        <h1>Supplier Management</h1>
        {!showForm && (
          <button 
            className="btn-primary"
            onClick={() => setShowForm(true)}
          >
            + Add New Supplier
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showForm && (
        <div className="supplier-form-container">
          <div className="supplier-form">
            <h2>{editingId ? 'Edit Supplier' : 'Add New Supplier'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Supplier Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., RHI Magnesita"
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., RHIMAG001"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Vienna, Austria"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    placeholder="contact@supplier.com"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                    placeholder="+43 1 234 5678"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingId ? 'Update Supplier' : 'Create Supplier'}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading suppliers...</div>
      ) : suppliers.length === 0 ? (
        <div className="empty-state">
          <p>No suppliers yet. Create one to get started!</p>
        </div>
      ) : (
        <div className="suppliers-grid">
          {suppliers.map(supplier => (
            <div key={supplier.supplier_id} className="supplier-card">
              <div className="card-header">
                <h3>{supplier.supplier_name}</h3>
                <span className="supplier-code">{supplier.supplier_code}</span>
              </div>
              <div className="card-body">
                <p>
                  <strong>Location:</strong> {supplier.address}
                </p>
                {supplier.email && (
                  <p>
                    <strong>Email:</strong> <a href={`mailto:${supplier.email}`}>{supplier.email}</a>
                  </p>
                )}
                {supplier.phone && (
                  <p>
                    <strong>Phone:</strong> {supplier.phone}
                  </p>
                )}
              </div>
              <div className="card-actions">
                <button 
                  className="btn-edit"
                  onClick={() => handleEdit(supplier)}
                >
                  Edit
                </button>
                <button 
                  className="btn-delete"
                  onClick={() => handleDelete(supplier.supplier_id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupplierManagement;
